import { FormikProps, useFormikContext } from "formik";
import { getYearlyTffAmount } from "../../vehicleUtils";
import {
  MotorcycleProductPriceFormValues,
  MotorcycleTexts,
} from "../motorcycleInterface";
import {
  getAllCoveragesOrKaskoCoverage,
  getDeductible,
} from "../motorcycleUtils/motorcycleUtils";

import { H3 } from "App/global-style";
import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { MultipleProductCoverages } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/MultipleProductCoverages";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { MotorcycleCoverage } from "Store/interfaces/products/coverage";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import React from "react";
import { useSelector } from "react-redux";
import { filterCoveragesOnCoverage } from "../../../HouseProducts/houseProductsUtils";
import { TFFInfoBox } from "../../components/TFFInfoBox";
import { DEFAULT_DEDUCTIBLE_MOTORCYCLE } from "../motorcycleConstants";
import { Deductible } from "./Deductible";

interface Props {
  readonly formProps: FormikProps<MotorcycleProductPriceFormValues>;
  readonly canBuyAllCoverages: boolean;
  readonly texts: MotorcycleTexts;
  readonly lang: TextLanguage;
}

export const MotorcycleCoverages = (props: Props): React.ReactElement => {
  const formState = useFormikContext<MotorcycleProductPriceFormValues>();
  const { formProps, canBuyAllCoverages, texts, lang } = props;
  const { isLoading } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );
  const { motorcycle: coverageCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);
  const coverages = filterCoveragesOnCoverage(
    Object.values(MotorcycleCoverage),
    getAllCoveragesOrKaskoCoverage(
      canBuyAllCoverages,
      MotorcycleCoverage.MOTORCYCLE_FULL_OWN_DAMAGE,
    ),
  );

  if (coverages.length === 0) {
    return <SystemErrorAlert alertId="no-coverages" />;
  }

  const tffAmount = getYearlyTffAmount(formState.values.coverage, coverages);

  return (
    <>
      <Deductible
        formProps={formProps}
        coverages={coverages}
        texts={texts.formText.checkPrice}
        lang={lang}
      />

      <H3>{texts.formText.checkPrice.coverage.label[lang]}</H3>
      <MultipleProductCoverages
        fieldName="coverage"
        coverages={coverages}
        coverageStandards={Object.values(MotorcycleCoverage)}
        priceFilterValue={formProps.values.deductible}
        product={ProductBackendName.MOTORCYCLE}
        priceIsLoading={isLoading}
        textComponents={coverageCardTextComponents}
        alertLabel={
          "Delkasko og ansvar kan ikke velges når du har lån med sikkerhet i kjøretøyet."
        }
        canOnlyBuyKasko={!canBuyAllCoverages}
      />

      <TFFInfoBox fee={tffAmount} />

      <CurrentPriceMessage
        productBackendName={ProductBackendName.MOTORCYCLE}
        coverages={coverages}
        selectedPrices={[
          {
            selectedCoverage: formState.values.coverage,
            selectedDeductible: getDeductible(formState.values),
            defaultDeductible: DEFAULT_DEDUCTIBLE_MOTORCYCLE,
          },
        ]}
      />
    </>
  );
};
